/**
 * #10 購物車 / 訂單 / 商品 API client (Phase 1)
 */
import { api } from "./client";
import type { Address } from "./members";

export interface Product {
  id: string;
  name: string;
  spec: string;
  price: number;
  tagline: string;
  image_url: string;
  category: string;
}

export interface ProductDetailRow {
  label?: string;   // 有 label = 規格列(label + value);否則為段落(text)
  value?: string;
  text?: string;
  muted?: boolean;  // 段落是否次要(灰字小一號)
}

export interface ProductDetailSection {
  title: string;
  rows: ProductDetailRow[];
}

export interface ProductDetail {
  title: string;
  subtitle: string;
  intro: string;
  sections: ProductDetailSection[];
}

export interface ProductCategory {
  key: string;
  title: string;
  subtitle: string;
  detail?: ProductDetail | null;   // 完整商品詳情(目前只有鮮燉;無則 null)
  items: Product[];
}

export interface CartItem {
  product_id: string;
  name: string;
  spec: string;
  price_each: number;
  qty: number;
  subtotal: number;
  mode: "one_time" | "subscription";
  image_filename?: string;
}

export interface Cart {
  items: CartItem[];
  total: number;
  items_count: number;
}

export interface OrderItem {
  product_id: string;
  name: string;
  spec: string;
  price_each: number;
  qty: number;                  // 件(商業單位,單價以此計)
  subtotal: number;
  mode: "one_time" | "subscription";
  bottle_count?: number;        // 一件 = 幾瓶(後台 GET 動態補,出貨換算用)
}

export interface OrderTracking {
  carrier: string;   // "blackcat_room" | "blackcat" | "post" | "other"(舊單可能還有 711/familymart/sf)
  number: string;
}

// 分批出貨 — 一筆訂單可有多批出貨,每批出部分品項
export interface ShipmentLine {
  product_id: string;
  mode: string;
  name: string;
  qty: number;              // 新批=瓶數(unit="bottle");舊批=件數(無 unit)
  unit?: string;            // "bottle" = qty 已是瓶;缺 = 舊批(件)
  bottles?: number;         // 後台 GET 動態補:本行換算後的瓶數(顯示用)
}

export interface Shipment {
  shipment_id: string;
  lines: ShipmentLine[];
  carrier: string;
  number: string;
  ship_date: string;        // YYYY-MM-DD
  shipped_at?: string;      // server ts(系統記錄的出貨時間)
  note?: string;
  operator?: string;
}

// 訂單用的推薦碼 / 優惠碼 / 顧問碼 快照
export interface AppliedCode {
  code: string;
  type: "member" | "campaign" | "consultant";
  label: string;
  owner_uid: string;
  discount_amount: number;
}

// 會員端 /api/orders(列表、單筆、建單回應)只回白名單欄位 — 後端 order_service.PUBLIC_ORDER_FIELDS。
// member_uid / discount_note / admin_note / payment、shipments[].note / operator、applied_code.owner_uid
// 只有 /api/admin/orders 會回(後台頁共用這個 type)。會員端頁面要讀新欄位,後端白名單要一起加。
export interface Order {
  order_id: string;
  member_uid: string;
  items: OrderItem[];
  subtotal?: number;            // 折前原價合計(新欄位;舊單沒有時視為 = total)
  discount_amount?: number;     // 折抵金額(campaign 碼 或 後台手動折讓)
  discount_note?: string;       // 折讓原因(後台手動折讓時填,對帳 / audit)
  total: number;                // 折後實收
  applied_code?: AppliedCode;   // 本單用的碼(若有)
  address: Address;
  phone: string;
  name: string;
  payment_method: string;
  status: string;
  note: string;
  tracking?: OrderTracking;     // 舊單單筆出貨(legacy);新流程改用 shipments
  shipments?: Shipment[];       // 分批出貨批次清單
  admin_note?: string;
  created_at?: string;
  ship_date?: string;     // YYYY-MM-DD,shipped 後寫入
  shipped_at?: string;    // server ts,shipped 當下
  payment?: {             // 金流入帳資訊(mark_order_paid 寫入;mock 單沒有)
    provider?: string;    // "jkopay" = 街口收款單(後台退款鈕依此判斷)
    refunds?: {
      refund_order_id?: string;
      refund_tradeNo?: string;
      amount: number;
      refund_time?: string;
    }[];
  };
}

// ── 商品(公開,不必 JWT)─────────────────────
export async function getProducts(): Promise<ProductCategory[]> {
  const { data } = await api.get("/api/products");
  return data.categories;
}

// ── 購物車 ────────────────────────────────
export async function getCart(): Promise<Cart> {
  const { data } = await api.get("/api/cart");
  return data;
}

export async function addToCart(product_id: string, qty: number = 1, mode: "one_time" | "subscription" = "one_time") {
  const { data } = await api.post("/api/cart/items", { product_id, qty, mode });
  return data;
}

export async function updateCartQty(product_id: string, qty: number, mode: "one_time" | "subscription" = "one_time") {
  const { data } = await api.patch("/api/cart/items", { product_id, qty, mode });
  return data;
}

export async function removeFromCart(product_id: string, mode: "one_time" | "subscription" = "one_time") {
  const { data } = await api.delete("/api/cart/items", { data: { product_id, mode } });
  return data;
}

// ── 訂單 ──────────────────────────────────
export async function placeOrder(payload: {
  payment_method?: string;
  address_override?: Address;
  note?: string;
  code?: string;
  idempotency_key?: string;    // 冪等 key(uuid)— 同 key 重送回原本那筆,不會重複建單/入帳
}): Promise<{ status: string; order_id?: string; order?: Order; code_status?: string }> {
  const { data } = await api.post("/api/orders", payload);
  return data;
}

// 推薦碼 / 優惠碼 結帳前驗證(回折扣預覽)
export interface CodeValidation {
  status: string;   // ok / not_found / inactive / own_code / not_new_customer / expired / not_started / usage_exceeded / empty
  code?: string;
  type?: "member" | "campaign" | "consultant";
  label?: string;
  discount_amount?: number;
  message?: string;
}

export async function validateCode(code: string): Promise<CodeValidation> {
  const { data } = await api.post("/api/orders/validate-code", { code });
  return data;
}

export async function getOrder(order_id: string): Promise<Order> {
  const { data } = await api.get(`/api/orders/${order_id}`);
  return data.order;
}

export async function getMyOrders(): Promise<Order[]> {
  const { data } = await api.get("/api/orders");
  return data.orders;
}
